/**
 * OBS-3 · Migas de pan y saneado de los reportes de error. Puro y sin DOM.
 *
 * Un stack trace dice DÓNDE reventó. Casi nunca dice por qué, porque el estado
 * que lo provocó ya no está: qué pantalla había abierta, si acababa de
 * reconectar, si el servidor había mandado un aviso de reinicio. Las migas son
 * ese contexto — las últimas cosas que pasaron antes del fallo — y en un beta
 * cerrado, donde no puedes pedirle a nadie que reproduzca el bug, suelen ser la
 * diferencia entre arreglarlo y archivarlo.
 *
 * Lo que NO llevan: nada que escriba una persona. Nombres, chat y contraseñas se
 * quedan fuera por construcción, porque un sistema de errores es el sitio más
 * fácil del mundo para filtrar datos personales sin darse cuenta.
 */

/** Categoría de una miga, para poder filtrarlas al leer el reporte. */
export type CrumbKind = "nav" | "net" | "ui" | "game";

export interface Breadcrumb {
  /** Ms desde que arrancó la sesión (relativo: no delata la hora local). */
  t: number;
  kind: CrumbKind;
  /** Mensaje corto y de vocabulario CERRADO (nunca texto de un usuario). */
  msg: string;
}

/** Cuántas migas se conservan. Suficiente para el minuto anterior al fallo sin
 *  inflar cada reporte. */
export const MAX_CRUMBS = 25;

/** Tope de longitud de una miga (son etiquetas, no frases). */
const MAX_CRUMB_LEN = 80;

/**
 * Anillo de migas. Se sobrescribe solo: nunca crece, así que dejarlo puesto
 * toda la sesión no cuesta memoria que vaya a más.
 */
export class BreadcrumbTrail {
  private readonly items: Breadcrumb[] = [];
  private readonly startedAt: number;
  private readonly now: () => number;

  constructor(now: () => number = () => Date.now()) {
    this.now = now;
    this.startedAt = now();
  }

  add(kind: CrumbKind, msg: string): void {
    this.items.push({
      t: Math.max(0, Math.round(this.now() - this.startedAt)),
      kind,
      msg: String(msg).slice(0, MAX_CRUMB_LEN),
    });
    if (this.items.length > MAX_CRUMBS) this.items.shift();
  }

  /** Copia de las migas actuales, de la más vieja a la más nueva. */
  snapshot(): Breadcrumb[] {
    return this.items.map((c) => ({ ...c }));
  }

  get size(): number {
    return this.items.length;
  }
}

/**
 * Quita de una URL todo lo que pueda identificar a alguien, conservando la
 * FORMA (qué ruta y qué parámetros había), que es lo único que ayuda a depurar.
 *
 * Es imprescindible: la URL del juego lleva `?join=<sala>` y `?region=`, y en
 * enlaces compartidos puede llevar el nombre del jugador. Mandarla tal cual
 * metería en el sistema de errores exactamente los datos que docs/PRIVACIDAD.md
 * dice que no guardamos.
 */
export function redactUrl(pathname: string, search: string): string {
  if (!search || search === "?") return pathname;
  const keys: string[] = [];
  for (const pair of search.replace(/^\?/, "").split("&")) {
    if (!pair) continue;
    const key = pair.split("=")[0];
    if (key) keys.push(key);
  }
  // Solo los NOMBRES de los parámetros: saber que venía por `?join=` explica el
  // camino que siguió el jugador; saber qué sala era no aporta nada.
  return keys.length > 0 ? `${pathname}?${keys.join("&")}=…` : pathname;
}

/**
 * Firma de un error, para no mandar el mismo mil veces.
 *
 * Incluye la primera línea del stack porque el mismo mensaje desde dos sitios
 * distintos son dos bugs distintos; y no incluye más porque, si no, un stack
 * con números de línea que cambian entre builds parecería un bug nuevo cada vez.
 */
export function errorSignature(kind: string, message: string, stack: string): string {
  return `${kind}|${message}|${stack.split("\n")[0] ?? ""}`;
}

/**
 * Acorta un stack conservando el PRINCIPIO.
 *
 * Recortar por el final es lo correcto aquí, aunque suene al revés: los marcos
 * de arriba son los del código que falló, y los de abajo casi siempre son el
 * bucle de render y el arranque, que ya sabemos de memoria.
 */
export function trimStack(stack: string, max: number): string {
  return stack.length <= max ? stack : stack.slice(0, max) + "\n…";
}
